import * as React from "react";
import { useState } from "react";

import { WorkspaceCardSkeleton } from "@editor/components/editor/SkeletonLoaders";
import { useCurrentProjectContext } from "@editor/contexts/CurrentProjectContext";
import { useCurrentWorkspaceId } from "@editor/contexts/WorkspaceDashboardContext";
import useClickOutside from "@editor/hooks/useClickOutside";
import useCurrentUser from "@editor/hooks/useCurrentUser";
import { useLogAnalytics } from "@editor/hooks/useLogAnalytics";
import { useModal } from "@editor/hooks/useModal";
import { projectRouter } from "@editor/trpc/project";
import { getProjectName } from "@editor/utils/project-utils";

import Button from "@replo/design-system/components/button/Button";
import Separator from "@replo/design-system/components/Separator";
import twMerge from "@replo/design-system/utils/twMerge";
import { skipToken } from "@tanstack/react-query";
import {
  ChevronsUpDown,
  Plus,
  Repeat2,
  Settings,
  UserRoundPlus,
} from "lucide-react";
import { AnimatePresence, motion } from "motion/react";
import { useNavigate } from "react-router-dom";

import ShopMenu from "./ShopMenu";
import ShopSelectorHeader from "./ShopSelectorHeader";
import WorkspaceMenu from "./WorkspaceMenu";

const MENU_WIDTH = 285;
const MENU_HEIGHT = 380;
const ANIMATION_DURATION = 300;

const ShopSelector: React.FC<{
  isSidebarExpanded: boolean;
  setIsShopSelectorOpen: (isShopSelectorOpen: boolean) => void;
}> = ({ isSidebarExpanded, setIsShopSelectorOpen }) => {
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const rootRef = React.useRef<HTMLDivElement>(null);
  const { user } = useCurrentUser();
  const { data, isLoading } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery(user?.id ?? skipToken);
  const workspaceId = useCurrentWorkspaceId();
  const { project } = useCurrentProjectContext();
  const { openModal } = useModal();
  const navigate = useNavigate();
  const [selectedWorkspaceId, setSelectedWorkspaceId] = useState<string | null>(
    null,
  );
  const [isWorkspaceView, setIsWorkspaceView] = useState(false);
  const logEvent = useLogAnalytics();

  const { data: workspaces } =
    projectRouter.listGroupedByWorkspaceForUser.useQuery(user?.id ?? skipToken);

  const currentWorkspace = React.useMemo(
    () => data?.find((workspace) => workspace.workspaceId === workspaceId),
    [workspaceId, data],
  );

  const currentShopName = (() => {
    if (project) {
      return getProjectName(project);
    }
    if (currentWorkspace?.workspaceName) {
      return currentWorkspace.workspaceName;
    }
    return "Shop";
  })();

  function closeMenu() {
    setIsMenuOpen(false);
    setSelectedWorkspaceId(null);
    setIsWorkspaceView(false);

    // NOTE (Ben, 2025-09-16): We need to wait for the animation to complete before setting the state
    setTimeout(() => {
      setIsShopSelectorOpen(false);
    }, ANIMATION_DURATION);
  }

  function openMenu() {
    setIsMenuOpen(true);
    setIsShopSelectorOpen(true);
  }

  const onShopClick = (
    shopId: string,
    hostingType: "replo" | "shopify",
    shopName: string | undefined | null,
  ) => {
    closeMenu();
    navigate(
      hostingType === "replo"
        ? `/shops/${shopId}/home`
        : `/shops/${shopId}/site`,
    );

    logEvent("dashboard.shop.select", {
      toShopName: shopName ?? "",
      toShopId: shopId,
    });
  };

  useClickOutside(rootRef, closeMenu);

  const sortedWorkspaces = React.useMemo(() => {
    if (!workspaces) {
      return [];
    }

    // Sort workspaces: current workspace first, then alphabetically
    return workspaces.sort((a, b) => {
      if (a.workspaceId === workspaceId) {
        return -1;
      }
      if (b.workspaceId === workspaceId) {
        return 1;
      }

      const nameA = a.workspaceName?.trim().toLowerCase() ?? "";
      const nameB = b.workspaceName?.trim().toLowerCase() ?? "";
      return nameA.localeCompare(nameB);
    });
  }, [workspaces, workspaceId]);

  const selectedWorkspace =
    sortedWorkspaces.find(
      (workspace) => workspace.workspaceId === selectedWorkspaceId,
    ) ?? currentWorkspace;

  if (!user) {
    return null;
  }

  return (
    <motion.div
      ref={rootRef}
      className={twMerge(
        "border-t-0.5 border-l-0.5 flex flex-col overflow-hidden rounded-lg bg-white h-full border-transparent",
        isMenuOpen &&
          "border-t-0.5 border-r-0.5 border-b-0.5 -mt-0.25 pt-0.25 -ml-0.25 pl-0.25 border-border",
      )}
      initial={false}
      animate={{
        width: isMenuOpen ? MENU_WIDTH : "auto",
        height: isMenuOpen ? MENU_HEIGHT : 48,
      }}
      transition={{
        type: "spring",
        stiffness: 450,
        damping: 35,
        mass: 0.5,
        delay: isMenuOpen ? 0 : 0.08,
      }}
    >
      {/* Header  */}
      <div
        onClick={isMenuOpen ? closeMenu : openMenu}
        className="cursor-pointer flex-shrink-0 p-3"
        data-testid="select-workspace"
      >
        {isLoading ? (
          <WorkspaceCardSkeleton compact={!isSidebarExpanded} />
        ) : (
          <ShopSelectorHeader
            workspaceName={currentWorkspace?.workspaceName ?? ""}
            shopName={currentShopName}
            endEnhancer={<ChevronsUpDown size={16} className="text-muted" />}
            isSidebarExpanded={isMenuOpen ? true : isSidebarExpanded}
            disableConditionalStyles={isMenuOpen}
          />
        )}
      </div>

      {/* Menu Content */}
      <AnimatePresence>
        {isMenuOpen && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: "100%" }}
            exit={{ opacity: 0, height: 0 }}
            transition={{
              type: "spring",
              stiffness: 500,
              damping: 40,
              mass: 0.6,
              opacity: { delay: 0.05 },
            }}
            className="flex flex-col overflow-y-auto"
          >
            {/* Action Buttons */}
            <div className="px-3 pb-3">
              <div className="flex gap-2">
                <Button
                  variant="secondary"
                  startEnhancer={<Settings size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (project) {
                      closeMenu();
                      navigate(
                        `/shops/${project.id}/settings/workspace/general`,
                      );
                    }
                  }}
                >
                  Workspace Settings
                </Button>
                <Button
                  variant="secondary"
                  startEnhancer={<UserRoundPlus size={16} />}
                  onClick={(e) => {
                    e.stopPropagation();
                    if (project) {
                      closeMenu();
                      navigate(
                        `/shops/${project.id}/settings/workspace/members`,
                      );
                    }
                  }}
                >
                  Invite
                </Button>
              </div>
            </div>

            <Separator className="h-[0.5px]" />

            <div className="overflow-hidden flex-1">
              {isWorkspaceView || !selectedWorkspace ? (
                <WorkspaceMenu
                  sortedWorkspaces={sortedWorkspaces}
                  onShopClick={onShopClick}
                  setIsWorkspaceView={setIsWorkspaceView}
                  setSelectedWorkspaceId={setSelectedWorkspaceId}
                />
              ) : (
                <ShopMenu
                  selectedWorkspace={selectedWorkspace}
                  onShopClick={onShopClick}
                />
              )}
            </div>

            <div className="border-t-0.5 border-border px-4 py-3">
              {isWorkspaceView ||
              !selectedWorkspace ||
              sortedWorkspaces.length === 1 ? (
                <Button
                  variant="link"
                  startEnhancer={<Plus size={12} />}
                  onClick={() => openModal({ type: "addWorkspaceModal" })}
                  size="sm"
                  data-testid="new-workspace"
                >
                  New Workspace
                </Button>
              ) : (
                <Button
                  variant="link"
                  startEnhancer={<Repeat2 size={12} />}
                  onClick={() => {
                    setSelectedWorkspaceId(null);
                    setIsWorkspaceView(true);
                  }}
                  data-testid="switch-workspace"
                  size="sm"
                >
                  Switch Workspace
                </Button>
              )}
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.div>
  );
};

export default ShopSelector;
